import React, {useState} from 'react';

import './_comments.scss';
import {DEFAULT_USER_IMAGE} from '../../../constants/Assets';
import {registeredUser} from '../../../constants/userTypes';
import {translate} from '../../../services';
import {CommentObject, MugshotObject, mugshotExists} from '../../../shapes/comments';
import {Locale} from '../../../shapes/locale';
import {UserType} from '../../../shapes/user';
import ImageUrls from '../../../utils/design';
import getListOfRepliesStatus from '../../../utils/getListOfRepliesStatus';
import getTimeElapsedSinceCreation from '../../../utils/getTimeElapsedSinceCreation';
import {isNotUndefined, isEmpty, isUndefined} from '../../../utils/validation';
import Collapsible from '../../Reusable/Content/Collapsible/Collapsible';
import ConditionalCollapsible from '../../Reusable/Content/ConditionalCollapsible/ConditionalCollapsible';
import Icon from '../../Reusable/Icon/Icon';
import LazyImage from '../../Reusable/LazyImage/LazyImage';

import Comment from './Comment';
import CommentsForm, {CommentsFormData} from './CommentsForm';


export interface CommentsProps {
  comments?: CommentObject[];
  onSubmit: (data: CommentsFormData) => void;
  locale: Locale;
  isDesktop: boolean;
  onClickReturn: (commentId: number | undefined) => void;
  userType?: UserType;
  userId?: number;
  deleteComment: (commentId: number) => void;
  designId?: number;
  designFileName?: string;
  imagesHost: string;
  s3host: string;
  gardenHost: string;
}

const Comments = ({onSubmit, comments, onClickReturn, userType, userId, deleteComment, designId, designFileName, locale, isDesktop, imagesHost, s3host, gardenHost}: CommentsProps): JSX.Element => {
  const [replyIsClosed, setReplyIsClosed] = useState<boolean>(true);
  const [buttonId, setbuttonId] = useState<number>();

  const assignId = (commentId: number) => {
    setbuttonId(commentId);
    changeReplyState(commentId, buttonId);
  };

  const changeReplyState = (commentId: number, buttonId?: number) => {
    const replyIsOpen = replyIsClosed === false;
    const userClicksDifferentButton = commentId !== buttonId;

    if (replyIsOpen && userClicksDifferentButton) {
      setReplyIsClosed(false);
    } else {
      setReplyIsClosed(!replyIsClosed);
    }
  };

  const getImageSource = (mugshot: string | MugshotObject) => (mugshotExists(mugshot) ?
    ImageUrls.avatarDesignerImageUrl(gardenHost, mugshot.id, mugshot.filename) :
    `${imagesHost}/${DEFAULT_USER_IMAGE}`);

  const getCommentImage = (commentIncludesImage: Record<string, string> | string, imageLink: string, userName: string) => {
    if (commentIncludesImage) {
      return <div className='image-wrapper'>
        <LazyImage
          className='comment-image'
          src={imageLink}
          alt={translate('fabric.userSubmittedPhotos.commentPhotoAltText', {
            user: userName
          })} />
      </div>;
    }
  };

  const createComment = (commentsData: CommentObject[]) => (
    commentsData.map((commentData) => {
      const deletedComment = commentData.deleted;
      const commentId = commentData.id;
      const usersComment = deletedComment ? translate('designs.comments.deletedComment') : commentData.comment;
      const commentUserId = commentData.user_id;
      const userName = commentData.screen_name;
      const createdAt = new Date(commentData.created_at);
      const mugshot = commentData.mugshot;
      const commentsChildren = commentData.children;
      const parentId = commentData.parent_id;
      const commentIncludesImage = commentData.link || '';
      const imageLink = commentIncludesImage && ImageUrls.commentImageUrl(s3host, designId, designFileName, commentId);

      const allRepliesAreDeleted = getListOfRepliesStatus(commentsChildren);
      const deletedCommentShowsReplies = commentsChildren && !allRepliesAreDeleted && deletedComment;
      const repliesToDisplay = commentsChildren && createComment(commentsChildren);
      const timeElapsedSinceCreation = getTimeElapsedSinceCreation(locale, createdAt);
      const userOwnsComment = userId === commentUserId;
      const displayComment = (isNotUndefined(usersComment) && !deletedComment) || deletedCommentShowsReplies;
      const imageSource = getImageSource(mugshot);
      const isReplyFalse = (value: boolean | JSX.Element) => value === false;
      const everyReplyValueFalse = repliesToDisplay?.every(isReplyFalse);

      return (
        displayComment ?
          <li className='reply-item' key={commentId}>
            <Comment
              imageSource={imageSource}
              userName={userName}
              deletedComment={deletedComment}
              createdAt={createdAt}
              timeElapsedSinceCreation={timeElapsedSinceCreation}
              userId={userId}
              userType={userType}
              parentId={parentId}
              commentId={commentId}
              userOwnsComment={userOwnsComment}
              deleteComment={deleteComment}
              usersComment={usersComment}
              assignId={assignId}
              replyIsClosed={!replyIsClosed} />
            {getCommentImage(commentIncludesImage, imageLink, userName)}
            {(commentId === buttonId && !replyIsClosed) && <CommentsForm
              onSubmit={onSubmit}
              onClickReturn={onClickReturn}
              commentId={commentId}
              isReply={true}
              form={`comment-${commentId}-replies`}
              comments={comments} />}
            {(!everyReplyValueFalse && repliesToDisplay && !allRepliesAreDeleted) && <div className='replies-wrapper'>
              <Icon extensionClass='list-arrow-return' iconName='arrow-return' iconTitle={translate('designs.comments.reply')} />
              <ul className='replies-list'>{repliesToDisplay}</ul>
            </div>}
          </li> :
          false
      );
    }).reverse()
  );

  const commentsList: CommentObject[] = [];
  const isRegisteredUser = userType && registeredUser(userType);

  comments?.forEach((comment) => commentsList.push(comment));

  const commentsToDisplay = createComment(commentsList).filter((c) => (c !== false));
  const displayHeading = (!isEmpty(commentsToDisplay) || (isEmpty(commentsToDisplay) && isNotUndefined(userId) && isRegisteredUser)) && isDesktop;
  const heading = `${translate('designs.comments.heading')} (${commentsToDisplay.length})`;

  if (isUndefined(comments)) {
    return (
      <ConditionalCollapsible heading={heading} isDesktop={isDesktop}>
        <article className='b-comments' id='comments-section'>
          {displayHeading && <h3 className='h3'>{heading}</h3>}
          {isNotUndefined(userId) && isRegisteredUser && <CommentsForm
            onSubmit={onSubmit}
            onClickReturn={onClickReturn}
            isReply={false}
            form='comments'
            comments={comments} />}
        </article>
      </ConditionalCollapsible>
    );
  }

  return (
    <ConditionalCollapsible heading={heading} isDesktop={isDesktop}>
      <article className='b-comments' id='comments-section'>
        {displayHeading &&
        <h3 className='h3'>{heading}</h3>}
        {isNotUndefined(userId) && isRegisteredUser &&
        <CommentsForm
          onSubmit={onSubmit}
          onClickReturn={onClickReturn}
          isReply={false}
          form='comments'
          comments={comments} />}
        {!isEmpty(commentsToDisplay) &&
        <ul className='comments-list'>
          {isDesktop ? commentsToDisplay.slice(0, 2) : commentsToDisplay}
        </ul>}
        {isDesktop && !isEmpty(commentsToDisplay.slice(2)) && <Collapsible
          title={translate('designs.comments.readMore')}
          sizeClass='x-trigger-extra-small'
          subtitleClass='x-extra-space'
          collapsibleOpen={false}
          triggerType='plus'
          content={
            <ul className='comments-list'>
              {commentsToDisplay.slice(2)}
            </ul>
          } />}
      </article>
    </ConditionalCollapsible>
  );
};

export default Comments;
